import type { RichTextField, TextField } from '@core/jss-proxy';

/**
 * Parses and sanitizes text content from various field types
 * @param field - Input field that can be a TextField, RichTextField, or plain string
 * @returns Sanitized string with HTML tags removed, newlines stripped, and excess whitespace normalized
 * @public
 *
 * @example
 * // Plain string input
 * parseToolValue("Hello  World") // Returns "Hello World"
 *
 * // HTML content
 * parseToolValue("<p>Hello<br/>World</p>") // Returns "Hello World"
 *
 * // Rich text field
 * parseToolValue({ value: "<p>Hello\n\nWorld</p>" }) // Returns "Hello World"
 */
export function sanitizeFieldValue(field: TextField | RichTextField | string) {
  if (field == null) {
    return '';
  }

  const rawValue = typeof field === 'string' ? field : field.value;
  if (!rawValue) {
    return '';
  }

  return (
    rawValue
      // Remove HTML tags
      .replace(/<[^>]*>/g, '')
      // Remove all newlines
      .replace(/(\r\n|\n|\r)/g, '')
      // Normalize multiple spaces to single space
      .replace(/\s+/g, ' ')
      // Trim leading and trailing whitespace
      .trim()
  );
}
